import { Modal, Table, Tooltip, message } from "antd"
import dayjs from "dayjs"
import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import { useMemo } from "react"
import { FiTrash } from "react-icons/fi"
import styled from "styled-components"

import {
    deleteBookingAsAdmin,
    getExportSheet,
    updateBookingBio,
} from "../../../../../../api"
import BottomSheet from "../../../../../_shared/components/BottomSheet"
import StyledFloatingButton from "../../../../../_shared/components/FloatingButton"
import AppInput from "../../../../../_shared/components/FormItems/AppInput"
import { useStateValue } from "../../../../../_shared/context/AppStateStore"
import useRestResource from "../../../../../_shared/hooks/useRestResource"
import { getAge } from "../../../../../_shared/toolbox"
import TableInput from "./TableInput"

// ----------------------------------------------------------------------------

function _BookingsBottomSheet(props) {
    // -------------------------------------
    // Props destructuring
    // -------------------------------------

    const { open, onCancel, eventId } = props

    const [pagination, setPagination] = useState({ current: 1 })

    // -------------------------------------
    // Hooks (e.g. useState, useMemo ...)
    // -------------------------------------

    const [{ token }] = useStateValue()
    const [_event, fetchEvent] = useRestResource("/events", eventId, token)

    const event = useMemo(() => {
        return {
            ..._event,
            slots: _event?.slots?.map((slot) => ({ ...slot, key: slot?.id })),
        }
        // eslint-disable-next-line
    }, [_event])

    const [filteredBookings, setFilteredBookings] = useState()
    const [editableForm, setEditableForm] = useState([])
    const [nameFilter, setNameFilter] = useState("")

    // -------------------------------------
    // Effects
    // -------------------------------------

    useEffect(() => {
        editableForm && setFilteredBookings(editableForm)
        // eslint-disable-next-line
    }, [editableForm])

    useEffect(() => {
        event?.slots && setEditableForm([...(event?.slots ?? [])])
        // eslint-disable-next-line
    }, [event])

    useEffect(() => {
        if (!nameFilter) {
            setFilteredBookings(editableForm)
            return
        }
        const pat = new RegExp(nameFilter.toLocaleLowerCase())
        const newBookings = editableForm.filter(({ booker }) => {
            if (!booker) return false
            const fullName =
                `${booker?.name} ${booker?.lastName}`.toLocaleLowerCase()
            return pat.test(fullName)
        })
        setFilteredBookings(newBookings)
        //eslint-disable-next-line
    }, [nameFilter])

    useEffect(() => {
        fetchEvent()
        // eslint-disable-next-line
    }, [eventId])

    // -------------------------------------
    // Component functions
    // -------------------------------------

    function updateForm(key, field) {
        return (e) => {
            const index = editableForm?.findIndex(
                ({ key: itemKey }) => itemKey === key
            )
            if (typeof index !== "undefined" && editableForm[index]) {
                editableForm[index] = {
                    ...editableForm[index],
                    booker: {
                        ...editableForm[index].booker,
                        [field]: e.target.value,
                    },
                }
            }
            setEditableForm([...(editableForm ?? [])])
        }
    }

    function validateDateFormat(date) {
        const format = "YYYY-MM-DD"
        const pat = /[0-9]{4}-[0-9]{2}-[0-9]{2}/

        if (!pat.test(date) || !(dayjs(date, format).format(format) === date)) {
            throw new Error(
                "La data di nascita deve essere nel formato YYYY-MM-DD"
            )
        }
    }

    function handleSave(key) {
        return async () => {
            try {
                const booking = editableForm.find(
                    (item) => item.key === key
                )?.booker
                const { email, phone, birth, residence, name, lastName } =
                    booking
                validateDateFormat(birth)
                const newBody = {
                    email,
                    phone,
                    birth,
                    residence,
                    name,
                    lastName,
                }
                const { id } = booking
                await updateBookingBio(newBody, id, { adminToken: token })
            } catch (e) {
                message.error(e.message)
            }
        }
    }

    function getEditableValue(key, field) {
        const item = editableForm?.find((item) => item.key === key)
        return item?.booker && item.booker[field]
    }

    function getPaginationObject() {
        return {
            size: "small",
            total: filteredBookings?.length || event?.slots?.length,
            current: pagination.curent,
            pageSize: 10,
            showSizeChanger: false,
        }
    }

    function handleTableChange(pagination) {
        // fetchCatalogs({ pagination });
        setPagination(pagination)
    }

    function handleFilterChange(e) {
        setNameFilter(e.target.value)
    }

    function deleteBooking(bookingId) {
        async function execute() {
            try {
                await deleteBookingAsAdmin(bookingId, token)
                message.success("Prenotazione eliminata.")
                fetchEvent && fetchEvent()
            } catch (e) {
                message.error(
                    "Non è stato possibile eliminare la prenotazione."
                )
            }
        }
        Modal.confirm({
            onOk: execute,
            content: "Sei sicuro di voler eliminare questa prenotazione?",
            title: "Conferma operazione",
        })
    }

    function renderDeleteActions(row) {
        return row.booker ? (
            <StyledFloatingButton onClick={() => deleteBooking(row.booker.id)}>
                <FiTrash className="icn" />
            </StyledFloatingButton>
        ) : (
            ""
        )
    }

    async function exportToExcel() {
        try {
            const { url } = await getExportSheet(event?.id, token)

            // window.open(url)

            const a = document.createElement("a")
            a.href = url
            a.download = true
            document.body.appendChild(a) // we need to append the element to the dom -> otherwise it will not work in firefox
            a.click()
            a.remove() //afterwards we remove the element again
        } catch {
            message.error("Non è stato possibile esportare le prenotazioni.")
        }
    }

    // -------------------------------------
    // Component local variables
    // -------------------------------------

    return (
        <BottomSheet
            open={open}
            className={props.className}
            onCancel={onCancel}
            title="Prenotazioni"
            snapPoint={90}
        >
            <div className="row">
                <AppInput
                    className="name-filter"
                    value={nameFilter}
                    onChange={handleFilterChange}
                    placeholder="Ricerca per nome"
                />
                <span onClick={exportToExcel} className="excel">
                    Esporta prenotazioni
                </span>
            </div>
            <Table
                className="table"
                dataSource={filteredBookings}
                columns={getColumns(
                    getEditableValue,
                    updateForm,
                    handleSave,
                    renderDeleteActions,
                    _event
                )}
                rowKey={(row) => row.id}
                loading={false}
                pagination={getPaginationObject()}
                onChange={handleTableChange}
                checkStrictly={true}
            />
        </BottomSheet>
    )
}

// ----------------------------------------------------------------------------
// Component PropTypes and default props
// ----------------------------------------------------------------------------

_BookingsBottomSheet.propTypes = {
    className: PropTypes.string.isRequired,
    open: PropTypes.bool,
    onCancel: PropTypes.func,
    eventId: PropTypes.string,
}

_BookingsBottomSheet.defaultProps = {}

// ----------------------------------------------------------------------------

const BookingsBottomSheet = styled(_BookingsBottomSheet)`
    & {
        .row {
            display: flex;
            align-items: flex-end;
            justify-content: space-between;
            margin-bottom: 20px;
            .name-filter {
                width: 300px;
            }
            .excel {
                display: block;
                color: ${({ theme }) => theme.colors.backgroundPrimary};
                text-decoration: underline;
                cursor: pointer;
                font-size: 16px;
            }
        }
        .table {
            min-width: 100%;
            overflow: auto;
            .icn {
                font-size: 20px;
                color: ${(props) => props?.theme?.colors?.textPrimary};
            }
            tr {
                .row-actions {
                    display: flex;
                    justify-content: space-between;
                    transition: all 0.2s ease-in-out;
                    opacity: 0;
                }

                :hover {
                    .row-actions {
                        opacity: 1;
                    }
                }
            }
        }
    }
`
// ----------------------------------------------------------------------------

export default BookingsBottomSheet

function getColumns(
    getEditableValue,
    updateForm,
    save,
    renderDeleteAction,
    event
) {
    return [
        {
            title: "Location",
            dataIndex: "locationCode",
            key: "locationCode",
            render: (locationCode) => {
                const l = event?.locations?.find(
                    (loc) => locationCode === loc.code
                )
                const { city, address, province } = l ?? {}
                return (
                    <Tooltip
                        title={
                            <div style={{ textAlign: "center" }}>
                                {city}, {address} ({province})
                            </div>
                        }
                    >
                        {city}
                    </Tooltip>
                )
            },
        },
        {
            title: "Slot orario",
            dataIndex: "window",
            key: "window",
        },
        {
            title: "Note",
            dataIndex: "note",
            key: "note",
        },
        {
            title: "Nome",
            key: "name",
            // eslint-disable-next-line
            render: (record) =>
                record?.booker ? (
                    <TableInput
                        className="table-input"
                        value={getEditableValue(record.key, "name")}
                        onChange={updateForm(record.key, "name")}
                        onDoneEditing={save(record.key)}
                    />
                ) : (
                    ""
                ),
        },
        {
            title: "Cognome",
            key: "lastNAme",
            // eslint-disable-next-line
            render: (record) =>
                record?.booker ? (
                    <TableInput
                        className="table-input"
                        value={getEditableValue(record.key, "lastName")}
                        onChange={updateForm(record.key, "lastName")}
                        onDoneEditing={save(record.key)}
                    />
                ) : (
                    ""
                ),
        },
        {
            title: "Codice Fiscale",
            key: "code",
            //eslint-disable-next-line
            render: (text, record) => <>{record.booker?.user?.fiscalCode}</>,
        },
        {
            title: "E-Mail",
            key: "email",
            // eslint-disable-next-line
            render: (record) =>
                record?.booker ? (
                    <TableInput
                        className="table-input"
                        value={getEditableValue(record.key, "email")}
                        onChange={updateForm(record.key, "email")}
                        onDoneEditing={save(record.key)}
                    />
                ) : (
                    ""
                ),
        },
        {
            title: "Phone",
            key: "name",
            //eslint-disable-next-line
            // eslint-disable-next-line
            render: (record) =>
                record?.booker ? (
                    <TableInput
                        className="table-input"
                        value={getEditableValue(record.key, "phone")}
                        onChange={updateForm(record.key, "phone")}
                        onDoneEditing={save(record.key)}
                    />
                ) : (
                    ""
                ),
        },
        {
            title: "Residenza",
            key: "name",
            // eslint-disable-next-line
            render: (record) =>
                record?.booker ? (
                    <TableInput
                        className="table-input"
                        value={getEditableValue(record.key, "residence")}
                        onChange={updateForm(record.key, "residence")}
                        onDoneEditing={save(record.key)}
                        useTextArea
                    />
                ) : (
                    ""
                ),
        },
        {
            title: "Data di nascita",
            key: "birth",
            // eslint-disable-next-line
            render: (record) =>
                record?.booker ? (
                    <TableInput
                        className="table-input"
                        value={getEditableValue(record.key, "birth")}
                        onChange={updateForm(record.key, "birth")}
                        onDoneEditing={save(record.key)}
                    />
                ) : (
                    ""
                ),
        },
        {
            title: "Età",
            key: "age",
            //eslint-disable-next-line
            render: (text, record) => getAge(record?.booker?.birth) || "",
        },
        {
            key: "deleteAction",
            render: renderDeleteAction,
        },
    ]
}
